import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { SimulationConfig } from '@/types';

interface SimulationHUDProps {
  elapsedTime: number;
  timeScale: number;
  ballCount: number;
  bounceCount: number;
  ringsLeft: number;
  timeRemaining: number;
  currentRound: number;
  containerRadius: number;
  ballRadius: number;
  gravity: number;
  restitution: number;
  config: SimulationConfig;
}

export const SimulationHUD: React.FC<SimulationHUDProps> = ({ 
  elapsedTime, timeScale, ballCount, bounceCount, ringsLeft, 
  timeRemaining, currentRound, containerRadius, ballRadius, 
  gravity, restitution, config 
}) => {
  const [fps, setFps] = useState(0);
  
  useEffect(() => {
    let frameCount = 0;
    let lastTime = performance.now();
    let animationFrameId: number;

    const updateFps = () => {
      frameCount++;
      const now = performance.now();
      if (now - lastTime >= 1000) {
        setFps(Math.round((frameCount * 1000) / (now - lastTime)));
        frameCount = 0;
        lastTime = now;
      }
      animationFrameId = requestAnimationFrame(updateFps);
    };

    animationFrameId = requestAnimationFrame(updateFps);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  if (!config.showHUD) return null;

  const positionClasses = {
    TOP_LEFT: 'top-40 left-8 items-start',
    TOP_RIGHT: 'top-8 right-8 items-end',
    BOTTOM_LEFT: 'bottom-8 left-8 items-start',
    BOTTOM_RIGHT: 'bottom-8 right-8 items-end pb-16'
  };

  const hudItems = [
    { show: config.hudShowElapsedTime, label: 'Elapsed', value: `${(elapsedTime/1000).toFixed(2)}s` },
    { show: config.hudShowSpeed || config.hudShowTimeScale, label: 'Speed', value: `${timeScale.toFixed(2)}x` },
    { show: config.hudShowBallCount, label: 'Spheres', value: ballCount },
    { show: config.hudShowBounceCount, label: 'Bounces', value: bounceCount },
    { show: config.hudShowRingsLeft, label: 'Rings', value: ringsLeft, mode: ['ESCAPE', 'RHYTHMIC_ESCAPE'] },
    { show: config.hudShowTimeRemaining, label: 'Time', value: `${timeRemaining.toFixed(1)}s`, mode: ['ESCAPE', 'RHYTHMIC_ESCAPE'] },
    { show: config.hudShowFPS, label: 'FPS', value: fps },
    { show: config.hudShowCurrentRound, label: 'Round', value: currentRound, mode: ['SPACE_SPIRAL'] },
    { show: config.hudShowContainerRadius, label: 'Radius', value: `${(config.mode === 'RHYTHMIC_ESCAPE' ? config.rhythmicEscapeShieldRingRadius : containerRadius).toFixed(0)}px` },
    { show: config.hudShowBallRadius, label: 'Ball Size', value: `${ballRadius.toFixed(1)}px` },
    { show: config.hudShowGravity, label: 'Gravity', value: gravity.toFixed(3) },
    { show: config.hudShowRestitution, label: 'Bunciness', value: restitution.toFixed(2) },
    { show: config.hudShowBallMass, label: 'Mass', value: `${config.ballMass.toFixed(1)}kg` },
    { show: config.hudShowInitialSpeed, label: 'Init Speed', value: `${config.initialSpeed.toFixed(1)}` },
    { show: config.hudShowSpeedIncreaseRate, label: 'Accel', value: `${config.speedIncreaseRate.toFixed(4)}` },
    { show: config.hudShowAirResistance, label: 'Air Res', value: `${config.airResistance.toFixed(3)}` },
    { show: config.hudShowPhysicsSubsteps, label: 'Substeps', value: config.physicsSubsteps },
    { show: config.hudShowBallGrowthRate, label: 'Growth', value: `${config.ballGrowthRate.toFixed(4)}` },
    { show: config.hudShowContainerShrinkRate, label: 'Shrink', value: `${config.containerShrinkRate.toFixed(4)}` },
  ].filter(item => {
    if (!item.show) return false;
    if (item.mode) {
      const modes = Array.isArray(item.mode) ? item.mode : [item.mode];
      return modes.includes(config.mode);
    }
    return true;
  });

  if (hudItems.length === 0) return null;

  const isMinimal = hudItems.length > 6;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: config.hudOpacity }}
      className={`absolute z-40 pointer-events-none flex transition-all duration-300 ${positionClasses[config.hudPosition]} ${isMinimal ? 'flex-wrap max-w-[320px] sm:max-w-[450px] gap-2' : 'flex-col gap-2.5'}`}
      style={{ 
        transform: `scale(${config.hudScale})`,
        transformOrigin: config.hudPosition.includes('LEFT') ? 'left top' : 'right top'
      }}
    >
      {hudItems.map((item, idx) => (
        <motion.div 
          key={item.label}
          initial={{ x: config.hudPosition.includes('LEFT') ? -20 : 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: idx * 0.05 }}
          className={`${config.darkMode ? 'dark-hardware-surface text-white' : 'hardware-surface text-zinc-950'} glow-active flex flex-col items-center shadow-lg ${isMinimal ? 'px-3 py-1.5 min-w-[70px] sm:min-w-[90px]' : 'px-5 py-2.5 min-w-[110px] sm:min-w-[130px]'}`}
        >
          <span className={`uppercase tracking-[0.2em] leading-none mb-1.5 ${config.darkMode ? 'text-white/40' : 'text-zinc-950/40'} ${isMinimal ? 'text-[7px] font-medium' : 'text-[9px] font-medium'}`}>
            {item.label}
          </span>
          <span 
            id={`hud-val-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`font-mono font-medium tracking-tight leading-none ${isMinimal ? 'text-xs' : 'text-lg'}`}
          >
            {item.value}
          </span>
        </motion.div>
      ))}
    </motion.div>
  );
};
